import React from "react" // eslint-disable-line no-unused-vars
import { compact, chain, capitalize, startsWith, get, isNil } from "lodash"
import { getBetaInstrumentLabelAndTooltip } from "helpers/contributors"
import { getFirstAvailableCompositionDescription, getNMVTooltip, makeDataPath } from "./utils"
import {
  VALUE_FORMATS,
  METRIC_TYPES,
  RESIDUAL_RETURN,
  PERFORMANCE_CONTRIBUTION_CATEGORY_CURRENCY,
  PERFORMANCE_CONTRIBUTION_CATEGORY_PERCENT,
} from "./constants"
import { getFormatter } from "./formatters"
import MetricWithTooltip from "renderers/contributors-datagrid/components/metric-with-tooltip"
import { INSTRUMENT_TYPES } from "connected-components/dashboard/helpers"

const MERGED_NAME_PRIMARY_INSTRUMENT = [
  INSTRUMENT_TYPES.original,
  INSTRUMENT_TYPES.end,
  INSTRUMENT_TYPES.rebalanced,
  INSTRUMENT_TYPES.comparison,
  INSTRUMENT_TYPES.start,
  INSTRUMENT_TYPES.optimized,
  INSTRUMENT_TYPES.target,
]

function filterInstrumentColumnsForMergedDescription(instrumentColumns) {
  const withoutDelta = instrumentColumns.filter(column => column !== INSTRUMENT_TYPES.delta)
  if (withoutDelta.length <= 1) {
    return withoutDelta
  }
  const primary =
    MERGED_NAME_PRIMARY_INSTRUMENT.find(candidate => withoutDelta.includes(candidate)) ?? withoutDelta[0]
  return [primary]
}

export function getTotalReturnLabel({ format } = {}) {
  if (format === VALUE_FORMATS.CURRENCY) {
    return "Total Return ($)"
  }
  if (format === VALUE_FORMATS.PERCENT) {
    return "Total Return (%)"
  }
  return "Total Return"
}

export const ALPHA_RETURN_LABEL = "Alpha Return"

export const FACTOR_RETURN_LABEL = "Factor Return"

function buildPerformanceReturnMetric({
  id,
  dataPath,
  category,
  format,
  filterInstrumentColumns,
  ignoreMagnitudeOrdering,
  getReturnLabel,
  enhanceValue,
}) {
  return {
    id,
    label: getReturnLabel(),
    getReturnLabel,
    category,
    dataPath,
    ...format && { format },
    ...filterInstrumentColumns && { filterInstrumentColumns },
    ignoreMagnitudeOrdering,
    ...enhanceValue && { enhanceValue },
    isPerformanceMetric: true,
  }
}

/**
 * Creates an enhanceValue function that displays a metric with a tooltip referencing another metric's value.
 *
 * @param {string} referencedDataPath - Path to the referenced metric value (e.g., "performance.residualReturn")
 * @param {string} format - The VALUE_FORMATS type to determine formatter (with units included)
 * @param {Function} template - Function that takes formatted value and returns tooltip text
 * @returns {Function} An enhanceValue function that returns a React component with tooltip
 */
export function createMetricTooltip(referencedDataPath, format, template) {
  const enhanceValue = (item, value, metric, isAggregation = false) => {
    // Get the instrument suffix from the transformed metric
    const { instrumentSuffix = "" } = metric

    const transformedPath = makeDataPath(referencedDataPath, instrumentSuffix)

    const referencedValue = get(item, `values.${transformedPath}`)

    if (isNil(referencedValue)) return value

    const formatter = getFormatter(format, { includeUnits: true })
    const formatted = formatter(referencedValue)
    const tooltip = template(formatted)

    // Only show icon for aggregation rows (top-level totals), not for grouped contributors
    return <MetricWithTooltip value={value} tooltip={tooltip} showIcon={isAggregation} />
  }

  return enhanceValue
}

function getMetricsByCategory(factors, selectedCategory, modelMetadata) {
  if (selectedCategory.toLowerCase() === "factors") {
    const aggregatedCategories = {}
    return factors.reduce((acc, factor) => {
      if (!aggregatedCategories[factor.category]) {
        acc = [...acc, {
          id: factor.category,
          label: modelMetadata.categories.find(category => category.id === factor.category)?.label || capitalize(factor.category),
        }]
        aggregatedCategories[factor.category] = true
      }
      return acc
    }, [])
  }
  return factors.filter(factors => factors.category === selectedCategory) || []
}

export function getMarginalContributionMetrics({ isActive = false }) {
  return [
    {
      id: "totalRisk",
      category: "Total Risk",
      format: VALUE_FORMATS.PERCENT,
      dataPath: "marginal.total",
      label: isActive ? "Total % of Active Risk" : "Total % of Risk",
    },
    {
      id: "totalRiskContribution",
      category: "Total Risk",
      format: VALUE_FORMATS.PERCENT,
      dataPath: "marginal.risk.total.riskContribution",
      label: isActive ? "Active Risk Contribution" : "Risk Contribution",
    },
    {
      id: "totalRiskMarginalContribution",
      category: "Total Risk",
      format: VALUE_FORMATS.NUMBER,
      dataPath: "marginal.risk.total.marginalContribution",
      label: "Marginal Contribution",
    },
    {
      id: "totalRiskCorrelation",
      category: "Total Risk",
      format: VALUE_FORMATS.NUMBER,
      dataPath: "marginal.risk.total.correlation",
      label: isActive ? "Relative Correlation" : "Correlation",
    },
    {
      id: "totalRiskVolatility",
      category: "Total Risk",
      format: VALUE_FORMATS.PERCENT,
      dataPath: "marginal.risk.total.volatility",
      label: isActive ? "Relative Volatility" : "Volatility",
    },
    {
      id: "specificRisk",
      category: "Specific Risk",
      format: VALUE_FORMATS.PERCENT,
      dataPath: "marginal.specific",
      label: isActive ? "Specific % of Active Risk" : "Specific % of Risk",
    },
    {
      id: "specificRiskContribution",
      category: "Specific Risk",
      format: VALUE_FORMATS.PERCENT,
      dataPath: "marginal.risk.specific.riskContribution",
      label: isActive ? "Active Risk Contribution" : "Risk Contribution",
    },
    {
      id: "specificRiskMarginalContribution",
      category: "Specific Risk",
      format: VALUE_FORMATS.NUMBER,
      dataPath: "marginal.risk.specific.marginalContribution",
      label: "Marginal Contribution",
    },
    {
      id: "factorsRisk",
      category: "Factors Risk",
      format: VALUE_FORMATS.PERCENT,
      dataPath: "marginal.factors",
      label: isActive ? "Factors % of Active Risk" : "Factors % of Risk",
    },
    {
      id: "factorsRiskContribution",
      category: "Factors Risk",
      format: VALUE_FORMATS.PERCENT,
      dataPath: "marginal.risk.factors.riskContribution",
      label: isActive ? "Active Risk Contribution" : "Risk Contribution",
    },
    {
      id: "factorsRiskMarginalContribution",
      category: "Factors Risk",
      format: VALUE_FORMATS.NUMBER,
      dataPath: "marginal.risk.factors.marginalContribution",
      label: "Marginal Contribution",
    },
  ]
}

export function getFactorSensitivityMetrics({ modelMetadata, selectedCategory, factorSensitivityType }) {
  const metricsByCategory = getMetricsByCategory(modelMetadata.factors, selectedCategory, modelMetadata)

  const categoryLabel = startsWith(factorSensitivityType, "predicted") ? "Predicted Factor Sensitivity" : "Historical Factor Sensitivity"

  const factorSensitivityMetrics = metricsByCategory.map(metricCategory => ({
    category: categoryLabel,
    ...getFactorSensitivityMetricDetails(metricCategory, factorSensitivityType),
  }))
  return factorSensitivityMetrics
}

function getSelectedCategoryMetadata(selectedCategory, modelMetadata) {
  const selectedCategoryMetadata = modelMetadata.categories.find(category => category.id === selectedCategory)
  const shouldDisplayCategory = selectedCategory !== "factors" && !selectedCategoryMetadata?.singleFactor
  if (shouldDisplayCategory) {
    return selectedCategoryMetadata
  }
  return null
}

function getPerformanceMetrics({
  selectedMetricDisplay,
  modelMetadata,
  selectedCategory,
  filterInstrumentColumns,
}) {
  const isPerformanceCumulative = selectedMetricDisplay === METRIC_TYPES.CUMULATIVE_PERCENT
  const format = isPerformanceCumulative ? VALUE_FORMATS.PERCENT : VALUE_FORMATS.CURRENCY
  const category = isPerformanceCumulative
    ? PERFORMANCE_CONTRIBUTION_CATEGORY_PERCENT
    : PERFORMANCE_CONTRIBUTION_CATEGORY_CURRENCY
  const metricsByCategory = getMetricsByCategory(modelMetadata.factors, selectedCategory, modelMetadata)
  const selectedCategoryMetadata = getSelectedCategoryMetadata(selectedCategory, modelMetadata)

  return compact([
    ASSET_METRICS_BY_ID.totalReturn({ format, filterInstrumentColumns, category, ignoreMagnitudeOrdering: true }),
    ASSET_METRICS_BY_ID.specificReturn({ format, filterInstrumentColumns, category, ignoreMagnitudeOrdering: true }),
    ASSET_METRICS_BY_ID.factorsReturn({ format, filterInstrumentColumns, category, ignoreMagnitudeOrdering: true }),
    ASSET_METRICS_BY_ID.tradingReturn({ format, filterInstrumentColumns, category, ignoreMagnitudeOrdering: true }),
    ASSET_METRICS_BY_ID.residualReturn({ format, filterInstrumentColumns, category, ignoreMagnitudeOrdering: true }),
    selectedCategoryMetadata && {
      format,
      filterInstrumentColumns,
      category: selectedCategory,
      ...getPerformanceMetricDetails(selectedCategoryMetadata),
    },
    ...metricsByCategory.map(metricCategory => ({
      format,
      filterInstrumentColumns,
      category: "Performance Factors",
      ...getPerformanceMetricDetails(metricCategory),
    })),
  ])
}

function getPerformanceMetricDetails({ id, label }) {
  return {
    dataPath: `performance.${id}`,
    id,
    label,
    isPerformanceMetric: true,
  }
}

function getRiskCategoryMetrics({ modelMetadata, selectedCategory, categoryLabel }) {
  const metricsByCategory = getMetricsByCategory(modelMetadata.factors, selectedCategory, modelMetadata)
  const selectedCategoryMetadata = getSelectedCategoryMetadata(selectedCategory, modelMetadata)
  const category = categoryLabel || undefined

  return compact([
    ASSET_METRICS_BY_ID.riskTotalContribution({ category }),
    ASSET_METRICS_BY_ID.riskSpecificContribution({ category }),
    ASSET_METRICS_BY_ID.riskFactorsContribution({ category }),
    selectedCategoryMetadata && {
      category: selectedCategory,
      ...getRiskMetricDetails(selectedCategoryMetadata),
    },
    ...metricsByCategory.map(metricCategory => ({
      category: "Risk Factors",
      ...getRiskMetricDetails(metricCategory),
    })),
  ])
}

function getRiskMetricDetails(metricCategory) {
  return {
    dataPath: `risk.${metricCategory.id}`,
    format: VALUE_FORMATS.PERCENT,
    id: metricCategory.id,
    label: metricCategory.label,
  }
}

function getFactorSensitivityMetricDetails(metricCategory, factorSensitivityType) {
  const metricCategoryId = metricCategory.id
  return {
    dataPath: `${factorSensitivityType}.${metricCategoryId}`,
    format: VALUE_FORMATS.PERCENT,
    id: metricCategory.id,
    label: metricCategory.label,
  }
}

function getCompositionMetrics({ isActive = false, actionHandler = null }) {
  return [
    ASSET_METRICS_BY_ID.marketValue({ actionHandler }),
    ASSET_METRICS_BY_ID.percentEquity({ actionHandler, isActive }),
  ]
}

function getExposureMetrics({ modelMetadata, selectedCategory, selectedMetricDisplay }) {
  const format = selectedMetricDisplay === METRIC_TYPES.CURRENCY_EXPOSURE ? VALUE_FORMATS.CURRENCY : VALUE_FORMATS.NUMBER
  return chain(modelMetadata.factors)
    .filter(factor => factor.category === selectedCategory)
    .map(factor => ({
      category: "Exposure",
      format,
      dataPath: `exposure.${factor.id}`,
      id: factor.id,
      label: factor.label,
    }))
    .value()
}

function getBetaMetrics({ betaFields, betaInstrument, getBetaDataPath, modelMetadata, isIntraday }) {
  return [
    ...(betaFields.includes("predicted") ? [ASSET_METRICS_BY_ID.predictedBeta({ getBetaDataPath, modelName: modelMetadata.name })] : []),
    ...(betaFields.includes("historical") ? [ASSET_METRICS_BY_ID.historicalBeta({ getBetaDataPath, modelName: modelMetadata.name })] : []),
    ...(betaInstrument && !isIntraday ? [ASSET_METRICS_BY_ID.dynamicPredictedBeta({ getBetaDataPath, betaInstrument })] : []),
  ]
}

export const ASSET_METRIC_CATEGORIES = {
  composition: getCompositionMetrics,
  exposure: getExposureMetrics,
  beta: getBetaMetrics,
  performance: getPerformanceMetrics,
  risk: getRiskCategoryMetrics,
  marginalContribution: getMarginalContributionMetrics,
  factorSensitivity: getFactorSensitivityMetrics,
  custom: metrics => metrics.items,
  contentSet: metrics => metrics.items,
}

export const ASSET_METRICS_BY_ID = {
  description: () => ({
    id: "description",
    label: "Name",
    format: VALUE_FORMATS.STRING,
    initialWidth: 200,
    dataPath: "composition.description",
    hideInstrumentName: true,
    dataGetter: getFirstAvailableCompositionDescription,
    onlyShowOnceInDownload: true,
    filterInstrumentColumns: filterInstrumentColumnsForMergedDescription,
  }),
  marketValue: ({ actionHandler = null }) => ({
    id: "marketValue",
    label: "Net MV ($)",
    category: "",
    format: VALUE_FORMATS.CURRENCY,
    dataPath: "marketValue.positionSetEconomicExposure",
    tooltipContents: ({ instrumentType }) => ({ values }) => getNMVTooltip(values, instrumentType),
    action: actionHandler ? (position, instrumentType) => actionHandler(position, instrumentType, "economicExposure") : null,
  }),
  percentEquity: ({ actionHandler = null, isActive = false, hasReferenceEquity = false }) => ({
    id: "percentEquity",
    label: isActive ? "Active % Equity" : (hasReferenceEquity ? "% Allocation" : "% Gross"),
    category: "",
    format: VALUE_FORMATS.PERCENT_100,
    dataPath: "composition.economicExposure",
    action: actionHandler ? (position, instrumentType) => actionHandler(
      position,
      instrumentType,
      instrumentType === "original" && isActive ? "activePercentEquity" : "positionPercentEquity",
    ) : null,
  }),
  predictedBeta: ({ getBetaDataPath, modelName }) => ({
    id: "predicted",
    label: "Predicted",
    category: "Beta",
    format: VALUE_FORMATS.NUMBER,
    tooltipContents: `Predicted Beta values are provided by the ${modelName} model.`,
    dataPath: getBetaDataPath("predicted"),
  }),
  historicalBeta: ({ getBetaDataPath, modelName }) => ({
    id: "historical",
    label: "Historical",
    category: "Beta",
    format: VALUE_FORMATS.NUMBER,
    tooltipContents: `Historical Beta values are provided by the ${modelName} model.`,
    dataPath: getBetaDataPath("historical"),
  }),
  dynamicPredictedBeta: ({ getBetaDataPath, betaInstrument }) => ({
    id: "dynamicPredictedBeta",
    category: "Beta",
    format: VALUE_FORMATS.NUMBER,
    dataPath: getBetaDataPath("dynamicPredicted"),
    ...getBetaInstrumentLabelAndTooltip(betaInstrument),
  }),
  totalReturn: ({
    format,
    filterInstrumentColumns,
    category,
    ignoreMagnitudeOrdering,
  }) => {
    const getReturnLabel = () => getTotalReturnLabel({ format })
    return buildPerformanceReturnMetric({
      id: "totalReturn",
      dataPath: "performance.totalReturn",
      category,
      format,
      filterInstrumentColumns,
      ignoreMagnitudeOrdering,
      getReturnLabel,
      enhanceValue: createMetricTooltip(
        `performance.${RESIDUAL_RETURN}`,
        format,
        (formatted) => `Total return includes a residual of ${formatted}`,
      ),
    })
  },
  specificReturn: ({
    format,
    filterInstrumentColumns,
    category,
    ignoreMagnitudeOrdering,
  }) => {
    const getReturnLabel = () => ALPHA_RETURN_LABEL
    return buildPerformanceReturnMetric({
      id: "specificReturn",
      dataPath: "performance.specificReturn",
      category,
      format,
      filterInstrumentColumns,
      ignoreMagnitudeOrdering,
      getReturnLabel,
    })
  },
  factorsReturn: ({
    format,
    filterInstrumentColumns,
    category,
    ignoreMagnitudeOrdering,
  }) => {
    const getReturnLabel = () => FACTOR_RETURN_LABEL
    return buildPerformanceReturnMetric({
      id: "factorsReturn",
      dataPath: "performance.factorsReturn",
      category,
      format,
      filterInstrumentColumns,
      ignoreMagnitudeOrdering,
      getReturnLabel,
    })
  },
  tradingReturn: ({ format, filterInstrumentColumns, category, ignoreMagnitudeOrdering }) => ({
    id: "tradingReturn",
    label: "Trading",
    category,
    dataPath: "performance.tradingReturn",
    ...format && { format },
    ...filterInstrumentColumns && { filterInstrumentColumns },
    hideIfEmpty: true,
    ignoreMagnitudeOrdering,
    isPerformanceMetric: true,
  }),
  residualReturn: ({ format, filterInstrumentColumns, category, ignoreMagnitudeOrdering }) => ({
    id: RESIDUAL_RETURN,
    label: "Residual",
    category,
    dataPath: `performance.${RESIDUAL_RETURN}`,
    ...format && { format },
    ...filterInstrumentColumns && { filterInstrumentColumns },
    hideIfEmpty: true,
    ignoreMagnitudeOrdering,
    downloadOnly: true,
    isPerformanceMetric: true,
  }),
  riskTotalContribution: ({ category = "Risk Decomposition" } = {}) => ({
    category,
    dataPath: "risk.totalContribution",
    format: VALUE_FORMATS.PERCENT,
    id: "totalDecompRisk",
    label: "Total",
  }),
  riskSpecificContribution: ({ category = "Risk Decomposition" } = {}) => ({
    category,
    dataPath: "risk.specific",
    format: VALUE_FORMATS.PERCENT,
    id: "specificDecompRisk",
    label: "Specific",
  }),
  riskFactorsContribution: ({ category = "Risk Decomposition" } = {}) => ({
    category,
    dataPath: "risk.factors",
    format: VALUE_FORMATS.PERCENT,
    id: "factorsDecompRisk",
    label: "Factors",
  }),
  securityExpectedReturn: ({ forecastId }) => ({
    id: "securityExpectedReturn",
    label: "Security Expected Return",
    category: "Forecast",
    format: VALUE_FORMATS.PERCENT,
    dataPath: `${forecastId}.securityExpectedReturn`,
  }),
  weightedExpectedReturn: ({ forecastId }) => ({
    id: "weightedExpectedReturn",
    label: "Weighted Expected Return",
    category: "Forecast",
    format: VALUE_FORMATS.PERCENT,
    dataPath: `${forecastId}.weightedExpectedReturn`,
  }),
  expectedPercentReturn: ({ forecastId }) => ({
    id: "expectedPercentReturn",
    label: "Expected Percent Return",
    category: "Forecast",
    format: VALUE_FORMATS.PERCENT,
    dataPath: `${forecastId}.expectedPercentReturn`,
  }),
}
